import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { supabase } from '@/lib/supabase';

interface DashboardStats {
  // Customers
  customersTotal: number;
  customersNew: number;
  customersFollowing: number;
  customersSigned: number;
  customersInvalid: number;
  // Articles
  articlesTotal: number;
  articlesPublished: number;
  articlesDraft: number;
  // Cases
  casesTotal: number;
  casesPublished: number;
  casesDraft: number;
  // FAQs
  faqsTotal: number;
  faqsPublished: number;
  faqsDraft: number;
}

const defaultStats: DashboardStats = {
  customersTotal: 0,
  customersNew: 0,
  customersFollowing: 0,
  customersSigned: 0,
  customersInvalid: 0,
  articlesTotal: 0,
  articlesPublished: 0,
  articlesDraft: 0,
  casesTotal: 0,
  casesPublished: 0,
  casesDraft: 0,
  faqsTotal: 0,
  faqsPublished: 0,
  faqsDraft: 0,
};

export default function AdminDashboard() {
  const [stats, setStats] = useState<DashboardStats>(defaultStats);
  const [recentCustomers, setRecentCustomers] = useState<{ id: number; name: string; phone: string; service: string; status: string; created_at: string }[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchStats = async () => {
      const [
        cTotalRes,
        cNewRes,
        cFollowingRes,
        cSignedRes,
        cInvalidRes,
        aTotalRes,
        aPubRes,
        aDraftRes,
        casTotalRes,
        casPubRes,
        casDraftRes,
        fTotalRes,
        fPubRes,
        fDraftRes,
      ] = await Promise.all([
        // Customers
        supabase.from('customers').select('id', { count: 'exact', head: true }),
        supabase.from('customers').select('id', { count: 'exact', head: true }).eq('status', 'new'),
        supabase.from('customers').select('id', { count: 'exact', head: true }).eq('status', 'following'),
        supabase.from('customers').select('id', { count: 'exact', head: true }).eq('status', 'signed'),
        supabase.from('customers').select('id', { count: 'exact', head: true }).eq('status', 'invalid'),
        // Articles (exclude deleted)
        supabase.from('articles').select('id', { count: 'exact', head: true }).neq('status', 'deleted'),
        supabase.from('articles').select('id', { count: 'exact', head: true }).eq('status', 'published'),
        supabase.from('articles').select('id', { count: 'exact', head: true }).eq('status', 'draft'),
        // Cases (exclude deleted)
        supabase.from('success_cases').select('id', { count: 'exact', head: true }).neq('status', 'deleted'),
        supabase.from('success_cases').select('id', { count: 'exact', head: true }).eq('status', 'published'),
        supabase.from('success_cases').select('id', { count: 'exact', head: true }).eq('status', 'draft'),
        // FAQs (exclude deleted)
        supabase.from('faqs').select('id', { count: 'exact', head: true }).neq('status', 'deleted'),
        supabase.from('faqs').select('id', { count: 'exact', head: true }).eq('status', 'published'),
        supabase.from('faqs').select('id', { count: 'exact', head: true }).eq('status', 'draft'),
      ]);

      setStats({
        customersTotal: cTotalRes.count ?? 0,
        customersNew: cNewRes.count ?? 0,
        customersFollowing: cFollowingRes.count ?? 0,
        customersSigned: cSignedRes.count ?? 0,
        customersInvalid: cInvalidRes.count ?? 0,
        articlesTotal: aTotalRes.count ?? 0,
        articlesPublished: aPubRes.count ?? 0,
        articlesDraft: aDraftRes.count ?? 0,
        casesTotal: casTotalRes.count ?? 0,
        casesPublished: casPubRes.count ?? 0,
        casesDraft: casDraftRes.count ?? 0,
        faqsTotal: fTotalRes.count ?? 0,
        faqsPublished: fPubRes.count ?? 0,
        faqsDraft: fDraftRes.count ?? 0,
      });

      const { data } = await supabase
        .from('customers')
        .select('id, name, phone, service, status, created_at')
        .order('created_at', { ascending: false })
        .limit(5);
      setRecentCustomers(data ?? []);
      setLoading(false);
    };
    fetchStats();
  }, []);

  const statusMap: Record<string, { label: string; cls: string }> = {
    new: { label: '未联系', cls: 'bg-amber-50 text-amber-600' },
    following: { label: '跟进中', cls: 'bg-blue-50 text-blue-600' },
    signed: { label: '已签单', cls: 'bg-green-50 text-green-600' },
    invalid: { label: '已失效', cls: 'bg-gray-100 text-gray-400' },
  };

  const customerContacted = stats.customersFollowing + stats.customersSigned + stats.customersInvalid;

  const summaryCards = [
    {
      to: '/admin/customers',
      icon: 'ri-user-heart-line',
      label: '客户管理',
      accent: '#c9a96e',
      total: stats.customersTotal,
      badge: stats.customersNew,
      badgeColor: 'bg-red-500',
      sub: `未联系 ${stats.customersNew} · 已跟进 ${customerContacted}`,
    },
    {
      to: '/admin/articles',
      icon: 'ri-article-line',
      label: '文章管理',
      accent: '#1a3a5c',
      total: stats.articlesTotal,
      sub: `已发 ${stats.articlesPublished} · 草稿 ${stats.articlesDraft}`,
    },
    {
      to: '/admin/cases',
      icon: 'ri-heart-line',
      label: '案例管理',
      accent: '#e08a8a',
      total: stats.casesTotal,
      sub: `已发 ${stats.casesPublished} · 草稿 ${stats.casesDraft}`,
    },
    {
      to: '/admin/faqs',
      icon: 'ri-questionnaire-line',
      label: 'FAQ管理',
      accent: '#7c9a6e',
      total: stats.faqsTotal,
      sub: `已发 ${stats.faqsPublished} · 草稿 ${stats.faqsDraft}`,
    },
  ];

  const customerBreakdown = [
    { label: '未联系', count: stats.customersNew, color: 'bg-amber-100 text-amber-700', bar: 'bg-amber-400' },
    { label: '跟进中', count: stats.customersFollowing, color: 'bg-blue-100 text-blue-700', bar: 'bg-blue-400' },
    { label: '已签单', count: stats.customersSigned, color: 'bg-green-100 text-green-700', bar: 'bg-green-400' },
    { label: '已失效', count: stats.customersInvalid, color: 'bg-gray-100 text-gray-500', bar: 'bg-gray-300' },
  ];

  const maxCustomerCount = Math.max(1, stats.customersTotal);

  return (
    <div className="p-4 md:p-8">
      <div className="mb-6 md:mb-8">
        <h1 className="text-xl md:text-2xl font-bold text-[#1a3a5c]">控制台</h1>
        <p className="text-gray-400 text-sm mt-1">RTB 管理后台 — 一站式内容与客户管理</p>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 md:gap-4 mb-6 md:mb-8">
        {summaryCards.map((item) => (
          <Link
            key={item.to}
            to={item.to}
            className="bg-white rounded-xl md:rounded-2xl p-4 md:p-5 hover:-translate-y-0.5 transition-all cursor-pointer"
          >
            <div className="flex items-center justify-between mb-3">
              <div className="w-8 h-8 md:w-9 md:h-9 flex items-center justify-center rounded-xl" style={{ background: `${item.accent}18` }}>
                <i className={`${item.icon} text-base md:text-lg`} style={{ color: item.accent }}></i>
              </div>
              {item.badge !== undefined && item.badge > 0 && (
                <span className={`${item.badgeColor} text-white text-xs font-bold px-2 py-0.5 rounded-full`}>{item.badge}</span>
              )}
            </div>
            {loading ? (
              <div className="w-12 h-7 bg-gray-100 rounded animate-pulse mb-1"></div>
            ) : (
              <div className="text-xl md:text-2xl font-bold text-[#1a3a5c]">{item.total}</div>
            )}
            <div className="text-xs text-gray-400 mt-0.5">{item.label} · {item.sub}</div>
          </Link>
        ))}
      </div>

      {/* Customer Breakdown */}
      <div className="bg-white rounded-xl md:rounded-2xl p-4 md:p-6 mb-6 md:mb-8">
        <div className="flex items-center justify-between mb-4">
          <h2 className="font-bold text-[#1a3a5c] text-sm md:text-base">客户状态分布</h2>
          <span className="text-xs text-gray-400">共 {stats.customersTotal} 位客户</span>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          {customerBreakdown.map((item) => (
            <div key={item.label} className={`${item.color} rounded-xl p-3 md:p-4`}>
              <div className="text-lg md:text-xl font-bold mb-1">{item.count}</div>
              <div className="text-xs mb-2">{item.label}</div>
              <div className="h-1.5 bg-white/50 rounded-full overflow-hidden">
                <div
                  className={`h-full rounded-full ${item.bar} transition-all duration-700`}
                  style={{ width: `${Math.round((item.count / maxCustomerCount) * 100)}%` }}
                ></div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Recent customers */}
      <div className="bg-white rounded-xl md:rounded-2xl p-4 md:p-6">
        <div className="flex items-center justify-between mb-5">
          <h2 className="font-bold text-[#1a3a5c] text-sm md:text-base">最新客户线索</h2>
          <Link to="/admin/customers" className="text-[#c9a96e] text-xs hover:underline cursor-pointer">查看全部</Link>
        </div>
        {loading ? (
          <div className="space-y-3">
            {[1, 2, 3].map((i) => (
              <div key={i} className="h-12 bg-gray-50 rounded-xl animate-pulse"></div>
            ))}
          </div>
        ) : recentCustomers.length === 0 ? (
          <div className="text-center py-10 text-gray-400 text-sm">
            <i className="ri-user-add-line text-3xl mb-2 block"></i>
            暂无客户线索
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-gray-400 text-xs border-b border-gray-100">
                  <th className="text-left pb-3 font-medium">姓名</th>
                  <th className="text-left pb-3 font-medium">电话</th>
                  <th className="text-left pb-3 font-medium hidden sm:table-cell">服务意向</th>
                  <th className="text-left pb-3 font-medium">状态</th>
                  <th className="text-left pb-3 font-medium hidden md:table-cell">时间</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-50">
                {recentCustomers.map((c) => {
                  const s = statusMap[c.status] ?? statusMap.new;
                  return (
                    <tr key={c.id} className="hover:bg-gray-50">
                      <td className="py-3 font-medium text-[#1a3a5c]">{c.name}</td>
                      <td className="py-3 text-gray-500">{c.phone}</td>
                      <td className="py-3 text-gray-500 hidden sm:table-cell">{c.service || '未填写'}</td>
                      <td className="py-3">
                        <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${s.cls}`}>{s.label}</span>
                      </td>
                      <td className="py-3 text-gray-400 text-xs hidden md:table-cell">{new Date(c.created_at).toLocaleDateString('zh-CN')}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}